import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import ClientContextSelector from './ClientContextSelector'
import { useNotificationContext } from '../../contexts/NotificationContext'

interface User {
  id: number
  username: string
  email: string
  full_name: string
  role: string
  is_active: boolean
  created_at: string
}

interface GlobalLayoutProps {
  token: string
  onLogout: () => void
  children: React.ReactNode
}

interface NavItemDef {
  to: string
  label: string
  icon: string
  roles: string[]
}

interface NavGroupDef {
  label: string
  items: NavItemDef[]
}

const ALL_ROLES = ['USER', 'URZEDNIK', 'KIEROWNIK', 'ADMIN', 'MASTER_ADMIN']
const STAFF_ROLES = ['URZEDNIK', 'KIEROWNIK', 'ADMIN', 'MASTER_ADMIN']

// Nawigacja pogrupowana tematycznie - czytelniejsza niż płaska lista 15 pozycji
const navigationGroups: NavGroupDef[] = [
  {
    label: 'Główne',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: '🏠', roles: ALL_ROLES },
      { to: '/reports', label: 'Raporty', icon: '📋', roles: ALL_ROLES },
      { to: '/realizations', label: 'Realizacje', icon: '🏗️', roles: STAFF_ROLES },
      { to: '/requirements', label: 'Zapotrzebowanie', icon: '📝', roles: ALL_ROLES },
    ]
  },
  {
    label: 'Operacje',
    items: [
      { to: '/issues', label: 'Zgłoszenia', icon: '📞', roles: ALL_ROLES },
      { to: '/services', label: 'Zlecenia prac', icon: '🔧', roles: ALL_ROLES },
      { to: '/services-display', label: 'Ekran zleceń', icon: '📺', roles: ALL_ROLES },
      { to: '/schedule', label: 'Grafik', icon: '📅', roles: ALL_ROLES },
    ]
  },
  {
    label: 'Teren',
    items: [
      { to: '/map', label: 'Mapa', icon: '🗺️', roles: ALL_ROLES },
      { to: '/infrastructure', label: 'Obiekty', icon: '🏭', roles: ALL_ROLES },
    ]
  },
  {
    label: 'Analizy',
    items: [
      { to: '/statistics', label: 'Statystyka', icon: '📊', roles: ALL_ROLES },
      { to: '/live-statistics', label: 'Statystyki Live', icon: '⚡', roles: ALL_ROLES },
    ]
  },
  {
    label: 'System',
    items: [
      { to: '/clients', label: 'Referaty', icon: '👥', roles: ['ADMIN', 'MASTER_ADMIN'] },
      { to: '/activity-logs', label: 'Logi', icon: '🗒️', roles: ['MASTER_ADMIN'] },
      { to: '/settings', label: 'Ustawienia', icon: '⚙️', roles: ALL_ROLES },
    ]
  },
]

export default function GlobalLayout({ token, onLogout, children }: GlobalLayoutProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [hasUnseenServices, setHasUnseenServices] = useState(false)
  const [hasUnseenRequirements, setHasUnseenRequirements] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [unseenServices, setUnseenServices] = useState<Array<{id: string, service_type: string, author_name: string, created_at: string}>>([])
  const [unseenRequirements, setUnseenRequirements] = useState<Array<{id: string, requirement_type: string, subject: string, author_name: string, created_at: string}>>([])
  const [loadingNotifications, setLoadingNotifications] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { showError } = useNotificationContext()

  useEffect(() => {
    fetchUserData()
  }, [])

  useEffect(() => {
    if (!loading && token) {
      checkUnseenServices()
      // Check every 30 seconds
      const interval = setInterval(checkUnseenServices, 30000)
      return () => clearInterval(interval)
    }
  }, [loading, token, user])

  // Zamykanie dropdownu powiadomień: klik poza obszarem (ref + contains) lub 20 s.
  // Celowo 'pointerdown' zamiast 'click': pointerdown kliknięcia otwierającego
  // odpala się ZANIM ten nasłuch powstanie, więc otwierające kliknięcie nigdy
  // nie zamknie dropdownu (w React 18 efekty potrafią wykonać się synchronicznie
  // jeszcze w trakcie bąbelkowania tego samego kliknięcia).
  const notifRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!notificationsOpen) return

    const timer = setTimeout(() => setNotificationsOpen(false), 20000)
    const handlePointerDown = (e: PointerEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotificationsOpen(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('pointerdown', handlePointerDown)
    }
  }, [notificationsOpen])

  const checkUnseenServices = async () => {
    // Sprawdza oba źródła powiadomień: zlecenia prac i zapotrzebowania
    try {
      const [srvResp, reqResp] = await Promise.all([
        fetch('/api/services/has-unseen', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/requirements/has-unseen', { headers: { 'Authorization': `Bearer ${token}` } }),
      ])
      if (srvResp.ok) {
        const data = await srvResp.json()
        setHasUnseenServices(data.has_unseen)
      }
      if (reqResp.ok) {
        const data = await reqResp.json()
        setHasUnseenRequirements(data.has_unseen)
      }
    } catch (error) {
      console.error('Error checking unseen notifications:', error)
    }
  }

  const loadUnseenServices = async () => {
    setLoadingNotifications(true)
    try {
      const [srvResp, reqResp] = await Promise.all([
        fetch('/api/services/unseen-list', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/requirements/unseen-list', { headers: { 'Authorization': `Bearer ${token}` } }),
      ])
      setUnseenServices(srvResp.ok ? await srvResp.json() : [])
      setUnseenRequirements(reqResp.ok ? await reqResp.json() : [])
    } catch (error) {
      console.error('Error loading notifications:', error)
      setUnseenServices([])
      setUnseenRequirements([])
    } finally {
      setLoadingNotifications(false)
    }
  }

  const toggleNotifications = async (e: React.MouseEvent) => {
    // stopPropagation: klik w dzwonek nie może dolecieć do document,
    // gdzie nasłuch "kliknięcia poza" natychmiast zamknąłby dropdown
    e.stopPropagation()
    if (notificationsOpen) {
      setNotificationsOpen(false)
      return
    }
    setNotificationsOpen(true)
    await loadUnseenServices()
  }

  const handleNotificationClick = (serviceId: string) => {
    setNotificationsOpen(false)
    navigate(`/services?openModal=${serviceId}`)
  }

  const handleRequirementClick = () => {
    setNotificationsOpen(false)
    navigate('/requirements')
  }

  // Wejście na moduł czyści jego powiadomienia (oznacza jako widziane).
  // Działa tylko przy zmianie ścieżki, żeby nie kasować kropki, która pojawi się
  // w trakcie przebywania na stronie.
  const prevPathRef = useRef<string | null>(null)
  useEffect(() => {
    const prev = prevPathRef.current
    prevPathRef.current = location.pathname
    if (prev === location.pathname || loading || !token) return

    const markSeen = (url: string) => {
      fetch(url, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }).catch(() => {})
    }
    if (location.pathname === '/services' || location.pathname.startsWith('/services/')) {
      markSeen('/api/services/seen')
      setHasUnseenServices(false)
    } else if (location.pathname === '/requirements' || location.pathname.startsWith('/requirements/')) {
      markSeen('/api/requirements/seen')
      setHasUnseenRequirements(false)
    }
  }, [location.pathname, loading, token])

  const getServiceTypeLabel = (serviceType: string) => {
    const labels: {[key: string]: string} = {
      'odbiór_przyłącza': 'Odbiór przyłącza',
      'częściowy_odbiór_przyłącza': 'Częściowy odbiór przyłącza',
      'wymiana_wodomierza': 'Wymiana wodomierza',
      'usunięcie_awarii': 'Usunięcie awarii',
      'zamknięcie_przyłącza': 'Zamknięcie przyłącza',
      'kontrola_stacji_uzdatniania_wody': 'Kontrola SU',
      'zlecenie_wewnętrzne': 'Zlecenie wewnętrzne',
      'skoordynacja': 'Koordynacja',
      'inne': 'Inne'
    }
    return labels[serviceType] || serviceType
  }

  const fetchUserData = async () => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const userData = await response.json()
        setUser(userData)
      } else {
        onLogout()
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
      onLogout()
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    // onLogout() (App) unieważnia refresh token na serwerze i czyści localStorage;
    // musi wykonać się PRZED usunięciem tokenu, inaczej nie ma czym autoryzować.
    onLogout()
    navigate('/login')
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">Ładowanie…</p>
        </div>
      </div>
    )
  }

  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(to + '/')

  // Grupy przefiltrowane po roli użytkownika; puste grupy znikają
  const visibleGroups = navigationGroups
    .map(g => ({
      ...g,
      items: g.items.filter(item => user?.role && item.roles.includes(user.role))
    }))
    .filter(g => g.items.length > 0)

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white shadow-2xl transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out xl:translate-x-0 xl:static xl:inset-0 xl:w-64 xl:shadow-lg overflow-y-auto overscroll-contain`}>
        <div className="flex flex-col h-full">
          {/* Logo + zamknięcie (mobile) */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100">
            <img
              src="/mnexus_logo 500 x 200.png"
              alt="MNexus Logo"
              className="h-10 w-auto"
            />
            <button
              onClick={() => setSidebarOpen(false)}
              className="xl:hidden p-2.5 -mr-1 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              aria-label="Zamknij menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-5">
            {visibleGroups.map((group) => (
              <div key={group.label}>
                <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest px-3 mb-1.5">
                  {group.label}
                </h3>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const active = isActive(item.to)
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setSidebarOpen(false)}
                        className={`relative flex items-center gap-3 px-3 min-h-[44px] rounded-xl text-sm transition-colors ${
                          active
                            ? 'bg-blue-50 text-blue-700 font-semibold'
                            : 'text-gray-600 font-medium hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100'
                        }`}
                        aria-current={active ? 'page' : undefined}
                      >
                        <span className="text-lg">{item.icon}</span>
                        <span className="truncate">{item.label}</span>
                        {/* Czerwona kropka dla nowych zleceń / zapotrzebowań */}
                        {((item.to === '/services' && hasUnseenServices) ||
                          (item.to === '/requirements' && hasUnseenRequirements)) && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full"></span>
                        )}
                        {active && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-600 rounded-r-full"></span>
                        )}
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User info and logout */}
          <div className="border-t border-gray-100 p-3 safe-bottom">
            <div className="flex items-center gap-3 px-2 py-2 mb-2">
              <div className="w-9 h-9 shrink-0 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-sm">
                <span className="text-white text-sm font-semibold">
                  {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {user?.full_name || user?.username}
                </p>
                <p className="text-xs text-gray-400">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full min-h-[44px] flex items-center justify-center gap-2 text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Wyloguj
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white/90 backdrop-blur shadow-sm border-b border-gray-100 sticky top-0 z-40">
          <div className="px-3 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16 gap-2">
              {/* Hamburger menu for mobile/tablet */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="xl:hidden flex items-center gap-2 px-3 min-h-[44px] rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <span className="sr-only">Otwórz menu</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <span className="text-sm font-medium">Menu</span>
              </button>

              {/* Notifications, context selector and logout */}
              <div className="flex-1 flex justify-end items-center gap-1.5 sm:gap-3">
                {/* Services Notifications */}
                <div className="relative flex items-center" ref={notifRef}>
                  <button
                    onClick={toggleNotifications}
                    className="relative min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200 rounded-xl transition-colors text-lg"
                    title="Powiadomienia o nowych zleceniach"
                    aria-label="Powiadomienia"
                  >
                    🔔
                    {(hasUnseenServices || hasUnseenRequirements) && (
                      <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white"></span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {notificationsOpen && (
                    <div
                      className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-1.5rem)] bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900">Powiadomienia</h3>
                        <button
                          onClick={(e) => { e.stopPropagation(); checkUnseenServices(); loadUnseenServices() }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 text-sm"
                          title="Odśwież powiadomienia"
                        >
                          🔄
                        </button>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {loadingNotifications ? (
                          <div className="px-4 py-5 text-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-xs text-gray-500 mt-2">Ładowanie…</p>
                          </div>
                        ) : (unseenServices.length > 0 || unseenRequirements.length > 0) ? (
                          <div>
                            {unseenServices.length > 0 && (
                              <div>
                                <div className="px-4 pt-2.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
                                  🔧 Zlecenia prac
                                </div>
                                <div className="divide-y divide-gray-50">
                                  {unseenServices.map((service) => (
                                    <button
                                      key={service.id}
                                      onClick={() => handleNotificationClick(service.id)}
                                      className="w-full text-left px-4 py-3 min-h-[56px] hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors"
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">
                                            {service.id} — {getServiceTypeLabel(service.service_type)}
                                          </p>
                                          <p className="text-xs text-gray-500 truncate">
                                            przez {service.author_name}
                                          </p>
                                        </div>
                                        <div className="mt-1.5 shrink-0">
                                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                        </div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {unseenRequirements.length > 0 && (
                              <div>
                                <div className="px-4 pt-2.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-widest border-t border-gray-100">
                                  📝 Zapotrzebowania
                                </div>
                                <div className="divide-y divide-gray-50">
                                  {unseenRequirements.map((req) => (
                                    <button
                                      key={req.id}
                                      onClick={handleRequirementClick}
                                      className="w-full text-left px-4 py-3 min-h-[56px] hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors"
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">
                                            {req.id} — {req.subject}
                                          </p>
                                          <p className="text-xs text-gray-500 truncate">
                                            przez {req.author_name}
                                          </p>
                                        </div>
                                        <div className="mt-1.5 shrink-0">
                                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                        </div>
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="px-4 py-6 text-center">
                            <div className="text-2xl mb-1">✅</div>
                            <p className="text-xs text-gray-500">Brak nowych powiadomień</p>
                          </div>
                        )}
                      </div>
                      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                        <p className="text-[11px] text-gray-400 text-center">
                          Kliknij pozycję, aby przejść do szczegółów
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <ClientContextSelector token={token} />
                {/* Wyloguj w nagłówku tylko na większych ekranach - na mobile jest w menu */}
                <button
                  onClick={handleLogout}
                  className="hidden sm:inline-flex min-h-[44px] items-center bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                >
                  Wyloguj
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="py-4 sm:py-6">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] xl:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}
